import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useEffect, useState } from "react";
import { apiService } from "../services/apiService";
import { CURRENCIES, DEFAULT_CURRENCY, getCurrencyLabel, getCurrencyPlaceholder, getCurrencySymbol } from "../utils/currency";
import StreamingThemePicker from "../Components/StreamingThemePicker";
import ScoreboardAppearanceEditor from '../Components/ScoreboardAppearanceEditor';
import { normalizePlayerNameSize } from '../utils/streamingTypography';

const SettingsPage = () => {
    const { club_id } = useParams();
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [settings, setSettings] = useState({});
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [activeTab, setActiveTab] = useState("general");
    const [unsavedChanges, setUnsavedChanges] = useState({});
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const GAME_TYPES = [
        {
            id: "snooker",
            label: "Snooker",
            badgeBg: "#dc3545",
            timer: {
                enabledKey: "snooker_shot_timer_enabled",
            },
        },
        {
            id: "pool",
            label: "Pool",
            badgeBg: "#198754",
            timer: {
                enabledKey: "pool_shot_timer_enabled",
                shotKey: "pool_shot_time",
                extKey: "pool_extension_time",
            },
        },
        {
            id: "us_pool",
            label: "US Pool",
            badgeBg: "#0dcaf0",
            timer: {
                enabledKey: "us_pool_shot_timer_enabled",
                shotKey: "us_pool_shot_time",
                extKey: "us_pool_extension_time",
            },
        },
        {
            id: "heyball",
            label: "Heyball",
            badgeBg: "#ffc107",
            timer: {
                enabledKey: "chinese_pool_shot_timer_enabled",
                shotKey: "chinese_pool_shot_time",
                extKey: "chinese_pool_extension_time",
            },
        },
    ];

    const defaultSettings = {
        default_currency: DEFAULT_CURRENCY,
        default_language: "en",
        time_zone: "UTC",
        club_operating_hours: { open: "09:00", close: "23:00" },
        snooker_pricing_per_game: "10.00",
        mini_snooker_pricing_per_game: "8.00",
        pool_pricing_per_game: "8.00",
        us_pool_pricing_per_game: "9.00",
        chinese_pool_pricing_per_game: "7.00",
        queue_tables_to_display: "8",
        queue_table_animation_duration: "3",
        queue_players_per_table: "10",
        queue_player_animation_duration: "2",
        code_activation: "false",
        paid_code: "",
        gifted_code: "",
        tournament_title: "",
        display_breaks: "false",
        streaming_theme: "welshopen",
        player_name_format: "full",
        player_name_size: 100,
        streaming_stats_mode: "auto",
        snooker_shot_timer_enabled: "false",
        pool_shot_timer_enabled: "true",
        pool_shot_time: "45",
        pool_extension_time: "15",
        us_pool_shot_timer_enabled: "true",
        us_pool_shot_time: "45",
        us_pool_extension_time: "15",
        chinese_pool_shot_timer_enabled: "true",
        chinese_pool_shot_time: "45",
        chinese_pool_extension_time: "15",
        qualifying_frames_enabled: "true",
    };

    const settingMetadata = {
        default_currency: { category: "General", type: "string" },
        default_language: { category: "General", type: "string" },
        time_zone: { category: "General", type: "string" },
        club_operating_hours: { category: "General", type: "json" },
        snooker_pricing_per_game: { category: "Financial", type: "number" },
        mini_snooker_pricing_per_game: { category: "Financial", type: "number" },
        pool_pricing_per_game: { category: "Financial", type: "number" },
        us_pool_pricing_per_game: { category: "Financial", type: "number" },
        chinese_pool_pricing_per_game: { category: "Financial", type: "number" },
        queue_tables_to_display: { category: "Game Management", type: "number" },
        queue_table_animation_duration: { category: "Game Management", type: "number" },
        queue_players_per_table: { category: "Game Management", type: "number" },
        queue_player_animation_duration: { category: "Game Management", type: "number" },
        code_activation: { category: "Game Management", type: "boolean" },
        paid_code: { category: "Game Management", type: "string" },
        gifted_code: { category: "Game Management", type: "string" },
        snooker_shot_timer_enabled: { category: "Game Management", type: "boolean" },
        pool_shot_timer_enabled: { category: "Game Management", type: "boolean" },
        pool_shot_time: { category: "Game Management", type: "number" },
        pool_extension_time: { category: "Game Management", type: "number" },
        us_pool_shot_timer_enabled: { category: "Game Management", type: "boolean" },
        us_pool_shot_time: { category: "Game Management", type: "number" },
        us_pool_extension_time: { category: "Game Management", type: "number" },
        chinese_pool_shot_timer_enabled: { category: "Game Management", type: "boolean" },
        chinese_pool_shot_time: { category: "Game Management", type: "number" },
        chinese_pool_extension_time: { category: "Game Management", type: "number" },
        qualifying_frames_enabled: { category: "Statistics", type: "boolean" },
        tournament_title: { category: "Streaming", type: "string" },
        display_breaks: { category: "Streaming", type: "boolean" },
        streaming_theme: { category: "Streaming", type: "string" },
        player_name_format: { category: "Streaming", type: "string" },
        player_name_size: { category: "Streaming", type: "number" },
        streaming_stats_mode: { category: "Streaming", type: "string" },
    };

    const tabs = [
        { id: "general", label: "General", icon: "⚙️", category: "General" },
        { id: "financial", label: "Financial", icon: "💰", category: "Financial" },
        { id: "game", label: "Game Management", icon: "🎮", category: "Game Management" },
        { id: "statistics", label: "Statistics", icon: "📈", category: "Statistics" },
        { id: "scoreboard", label: "Scoreboard design", icon: "◩", category: "Scoreboard", isNew: true },
        { id: "streaming", label: "Streaming", icon: "📺", category: "Streaming" },
    ];

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const data = await apiService.get(`/settings/${club_id || ""}`);

            let mergedSettings = { ...defaultSettings };
            if (data && data.settings && typeof data.settings === "object") {
                const fetchedSettings = data.settings;

                let hours = defaultSettings.club_operating_hours;
                if (fetchedSettings.club_operating_hours) {
                    if (typeof fetchedSettings.club_operating_hours === "string") {
                        try {
                            hours = JSON.parse(fetchedSettings.club_operating_hours);
                        } catch {
                            hours = defaultSettings.club_operating_hours;
                        }
                    } else {
                        hours = fetchedSettings.club_operating_hours;
                    }
                }

                const parsedSettings = {
                    ...fetchedSettings,
                    club_operating_hours: hours,
                    display_breaks: fetchedSettings.display_breaks !== undefined ? String(fetchedSettings.display_breaks) : defaultSettings.display_breaks,
                    code_activation: defaultSettings.code_activation,
                    paid_code: defaultSettings.paid_code,
                    gifted_code: defaultSettings.gifted_code,
                    tournament_title: fetchedSettings.tournament_title !== undefined ? String(fetchedSettings.tournament_title) : defaultSettings.tournament_title,
                    streaming_theme: fetchedSettings.streaming_theme !== undefined ? String(fetchedSettings.streaming_theme) : defaultSettings.streaming_theme,
                    player_name_format: fetchedSettings.player_name_format === "last" ? "last" : "full",
                    player_name_size: normalizePlayerNameSize(fetchedSettings.player_name_size),
                    streaming_stats_mode: ['auto', 'manual', 'always'].includes(fetchedSettings.streaming_stats_mode) ? fetchedSettings.streaming_stats_mode : 'auto',
                    snooker_shot_timer_enabled: fetchedSettings.snooker_shot_timer_enabled !== undefined ? String(fetchedSettings.snooker_shot_timer_enabled) : defaultSettings.snooker_shot_timer_enabled,
                    pool_shot_timer_enabled: fetchedSettings.pool_shot_timer_enabled !== undefined ? String(fetchedSettings.pool_shot_timer_enabled) : defaultSettings.pool_shot_timer_enabled,
                    pool_shot_time: fetchedSettings.pool_shot_time !== undefined ? String(fetchedSettings.pool_shot_time) : defaultSettings.pool_shot_time,
                    pool_extension_time: fetchedSettings.pool_extension_time !== undefined ? String(fetchedSettings.pool_extension_time) : defaultSettings.pool_extension_time,
                    us_pool_shot_timer_enabled: fetchedSettings.us_pool_shot_timer_enabled !== undefined ? String(fetchedSettings.us_pool_shot_timer_enabled) : defaultSettings.us_pool_shot_timer_enabled,
                    us_pool_shot_time: fetchedSettings.us_pool_shot_time !== undefined ? String(fetchedSettings.us_pool_shot_time) : defaultSettings.us_pool_shot_time,
                    us_pool_extension_time: fetchedSettings.us_pool_extension_time !== undefined ? String(fetchedSettings.us_pool_extension_time) : defaultSettings.us_pool_extension_time,
                    chinese_pool_shot_timer_enabled: fetchedSettings.chinese_pool_shot_timer_enabled !== undefined ? String(fetchedSettings.chinese_pool_shot_timer_enabled) : defaultSettings.chinese_pool_shot_timer_enabled,
                    chinese_pool_shot_time: fetchedSettings.chinese_pool_shot_time !== undefined ? String(fetchedSettings.chinese_pool_shot_time) : defaultSettings.chinese_pool_shot_time,
                    chinese_pool_extension_time: fetchedSettings.chinese_pool_extension_time !== undefined ? String(fetchedSettings.chinese_pool_extension_time) : defaultSettings.chinese_pool_extension_time,
                    qualifying_frames_enabled: fetchedSettings.qualifying_frames_enabled !== undefined ? String(fetchedSettings.qualifying_frames_enabled) : defaultSettings.qualifying_frames_enabled,
                };

                mergedSettings = { ...defaultSettings, ...parsedSettings };
            }

            const details = await apiService.post(`/club/details`, { id: club_id || "" });
            const u = details?.user || {};

            mergedSettings = {
                ...mergedSettings,
                code_activation: u.code_activation !== undefined ? (String(u.code_activation) === "1" || String(u.code_activation) === "true" ? "true" : "false") : mergedSettings.code_activation,
                paid_code: u.paid_code !== undefined && u.paid_code !== null ? String(u.paid_code) : mergedSettings.paid_code,
                gifted_code: u.gifted_code !== undefined && u.gifted_code !== null ? String(u.gifted_code) : mergedSettings.gifted_code,
            };

            setSettings(mergedSettings);
            setFormData(mergedSettings);
            setLogoPreview(u.logo ? `/storage/${u.logo}` : null);
        } catch (err) {
            setError(`Failed to fetch settings: ${err.message}`);
            setSettings(defaultSettings);
            setFormData(defaultSettings);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate("/login");
            return;
        }
        fetchSettings();
    }, [club_id, navigate]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get("tab");
        if (tab && ["general", "financial", "game", "statistics", "streaming", "scoreboard"].includes(tab)) {
            setActiveTab(tab);
        }
    }, [location.search]);

    const handleInputChange = (key, value, subKey = null) => {
        setError(null);
        setFormData((prev) => {
            if (subKey) {
                return {
                    ...prev,
                    [key]: { ...prev[key], [subKey]: value },
                };
            }
            return { ...prev, [key]: value };
        });

        const category = settingMetadata[key]?.category;
        if (category) {
            let isChanged = false;
            if (subKey) {
                isChanged = settings[key]?.[subKey] !== value;
            } else {
                isChanged = settings[key] !== value;
            }
            if (isChanged) {
                setUnsavedChanges((prev) => ({ ...prev, [category]: true }));
            } else {
                const categoryKeys = Object.keys(formData).filter(
                    (k) => settingMetadata[k]?.category === category && k !== key
                );
                const hasOtherChanges = categoryKeys.some(k => {
                    if (typeof formData[k] === 'object' && formData[k] !== null) {
                        return JSON.stringify(formData[k]) !== JSON.stringify(settings[k]);
                    }
                    return formData[k] !== settings[k];
                });
                if (!hasOtherChanges && !logoFile) {
                    setUnsavedChanges((prev) => ({ ...prev, [category]: false }));
                }
            }
        }
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onload = (e) => setLogoPreview(e.target.result);
            reader.readAsDataURL(file);
            setUnsavedChanges((prev) => ({ ...prev, General: true }));
        } else {
            setError('No file selected');
        }
    };

    const validateFormData = (key, value, type) => {
        if (key === "club_operating_hours") {
            if (!value?.open || !value?.close || !/^\d{2}:\d{2}$/.test(value.open) || !/^\d{2}:\d{2}$/.test(value.close)) {
                return "Invalid time format (e.g., 09:00)";
            }
            return null;
        }

        if (key === "paid_code" || key === "gifted_code") {
            if (value !== "" && !/^\d+$/.test(value)) return "Must be numeric digits only";
            if (value && value.length > 8) return "Max 8 digits";
            return null;
        }

        if (["pool_shot_time", "pool_extension_time", "us_pool_shot_time", "us_pool_extension_time", "chinese_pool_shot_time", "chinese_pool_extension_time"].includes(key)) {
            if (value !== "" && !/^\d+$/.test(value)) return "Must be a positive integer";
            if (value && (parseInt(value) < 1 || parseInt(value) > 300)) return "Must be between 1 and 300 seconds";
            return null;
        }

        switch (type) {
            case "number":
                if (!/^\d+(\.\d{1,2})?$/.test(value) && value !== "") {
                    return "Must be a valid number (e.g., 10.00)";
                }
                break;
            case "string":
                break;
            case "boolean":
                if (!["true", "false"].includes(value)) {
                    return "Must be a boolean value";
                }
                break;
            default:
                return null;
        }
        return null;
    };

    const handleSubmit = async (category) => {
        const passCodeKeys = ["code_activation", "paid_code", "gifted_code"];
        const isGame = category === "Game Management";

        const categoryKeys = Object.keys(formData).filter(
            (key) => settingMetadata[key]?.category === category
        );

        const settingsToUpdate = categoryKeys
            .filter((key) => !passCodeKeys.includes(key) || !isGame)
            .map((key) => ({
                key,
                value: key === "club_operating_hours" ? JSON.stringify(formData[key]) : formData[key],
                category: settingMetadata[key].category,
                type: settingMetadata[key].type,
            }));

        for (const key of categoryKeys) {
            const type = settingMetadata[key].type;
            const v = key === "club_operating_hours" ? formData[key] : formData[key];
            const validationError = validateFormData(key, v, type);
            if (validationError) {
                setError(`${key.replace(/_/g, " ")}: ${validationError}`);
                return;
            }
        }

        if (isGame && formData.code_activation === "true" && !formData.paid_code && !formData.gifted_code) {
            setError("When activation is ON, one of the codes must be set.");
            return;
        }

        try {
            // Handle logo upload for General category
            if (category === "General" && logoFile) {
                const logoFormData = new FormData();
                logoFormData.append('logo', logoFile);
                logoFormData.append('club_id', club_id || '');
                const logoResponse = await apiService.postFormData(`/settings/update-logo/${club_id || ""}`, logoFormData);
                setSuccess(logoResponse.message);
                setLogoFile(null);
                setLogoPreview(null);
                const fileInput = document.querySelector('input[type="file"]');
                if (fileInput) fileInput.value = '';
            }

            // Save other settings
            if (settingsToUpdate.length) {
                const result = await apiService.post(`/settings/batch/${club_id || ""}`, {
                    settings: settingsToUpdate,
                });
                if (result.success === false) throw new Error(result.message || 'Settings could not be saved');
            }

            if (isGame) {
                await apiService.post(`/club/change-codes`, {
                    id: club_id || "",
                    code_activation: formData.code_activation === "true" ? "1" : "0",
                    paid_code: formData.paid_code ?? "",
                    gifted_code: formData.gifted_code ?? "",
                });
            }

            setSuccess(`${category} settings updated successfully!`);
            setError(null);
            setUnsavedChanges((prev) => ({ ...prev, [category]: false }));

            const updatedSettings = { ...settings };
            settingsToUpdate.forEach((setting) => {
                if (setting.key === "club_operating_hours") {
                    updatedSettings[setting.key] = JSON.parse(setting.value);
                } else {
                    updatedSettings[setting.key] = setting.value;
                }
            });

            if (isGame) {
                updatedSettings.code_activation = formData.code_activation;
                updatedSettings.paid_code = formData.paid_code;
                updatedSettings.gifted_code = formData.gifted_code;
            }

            setSettings(updatedSettings);

            setTimeout(() => setSuccess(null), 3000);
            fetchSettings(); // Refresh settings after successful save
        } catch (err) {
            const msg = err?.response?.data?.message || `Failed to update ${category.toLowerCase()} settings`;
            setError(msg);
        }
    };

    const GameTimersTable = ({ games, formData, onChange }) => {
        const isTrue = (v) => String(v) === "true";
        return (
            <div className="game-timers-table card p-0">
                <div className="table-responsive">
                    <table className="table align-middle mb-0 w-100">
                        <thead>
                            <tr>
                                <th style={{ width: "22%" }}>Game</th>
                                <th style={{ width: "18%" }}>Enabled</th>
                                <th style={{ width: "30%" }}>Shot Time (sec)</th>
                                <th style={{ width: "30%" }}>Extension Time (sec)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {games.map((g) => {
                                const { enabledKey, shotKey, extKey } = g.timer;
                                const enabled = isTrue(formData[enabledKey]);
                                return (
                                    <tr key={g.id}>
                                        <td data-label="Game">
                                            <div className="d-flex align-items-center">
                                                <span className="avatar avatar-xs me-2" style={{ backgroundColor: g.badgeBg }}>
                                                    {g.label[0]}
                                                </span>
                                                <div className="fw-500">{g.label}</div>
                                            </div>
                                        </td>
                                        <td data-label="Enabled">
                                            <label className="form-check form-switch m-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={enabled}
                                                    onChange={(e) =>
                                                        onChange(enabledKey, e.target.checked ? "true" : "false")
                                                    }
                                                />
                                                <span className="form-check-label ms-2">
                                                    {enabled ? "Enabled" : "Disabled"}
                                                </span>
                                            </label>
                                        </td>
                                        <td data-label="Shot Time (sec)">
                                            {g.id === "snooker" ? (
                                                <div className="input-group">
                                                </div>
                                            ) : (
                                                <div className="input-group">
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={formData[shotKey] || ""}
                                                        onChange={(e) => onChange(shotKey, e.target.value)}
                                                        placeholder="45"
                                                        min="1"
                                                        max="300"
                                                        disabled={!enabled}
                                                    />
                                                    <span className="input-group-text">seconds</span>
                                                </div>
                                            )}
                                            <small className="form-hint d-block">{g.id === "snooker" ? "Not applicable" : "1–300"}</small>
                                        </td>
                                        <td data-label="Extension Time (sec)">
                                            {g.id === "snooker" ? (
                                                <div className="input-group">
                                                </div>
                                            ) : (
                                                <div className="input-group">
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={formData[extKey] || ""}
                                                        onChange={(e) => onChange(extKey, e.target.value)}
                                                        placeholder="15"
                                                        min="1"
                                                        max="300"
                                                        disabled={!enabled}
                                                    />
                                                    <span className="input-group-text">seconds</span>
                                                </div>
                                            )}
                                            <small className="form-hint d-block">{g.id === "snooker" ? "Not applicable" : "1–300"}</small>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="page-wrapper">
                <div className="page-body">
                    <div className="container-xl">
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                            <div className="text-center">
                                <div className="spinner-border text-primary mb-3" role="status" style={{ width: "3rem", height: "3rem" }}></div>
                                <h3 className="text-muted">Loading Settings...</h3>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <div className="page-pretitle">Configuration</div>
                            <h2 className="page-title">Club Settings</h2>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">
                    {error && (
                        <div className="alert alert-danger alert-dismissible fade show mb-3" role="alert">
                            <div className="d-flex">
                                <div>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon alert-icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" aria-hidden="true">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <path d="M12 9v2m0 4v.01"></path>
                                        <path d="M5 19h14a2 2 0 0 0 1.84 -2.75l-7.1 -12.25a2 2 0 0 0 -3.5 0l-7.1 12.25a2 2 0 0 0 1.75 2.75"></path>
                                    </svg>
                                </div>
                                <div className="ms-2">{error}</div>
                            </div>
                            <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close"></button>
                        </div>
                    )}

                    {success && (
                        <div className="alert alert-success alert-dismissible fade show mb-3" role="alert">
                            <div className="d-flex">
                                <div>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon alert-icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" aria-hidden="true">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <path d="M5 12l5 5l10 -10"></path>
                                    </svg>
                                </div>
                                <div className="ms-2">{success}</div>
                            </div>
                            <button type="button" className="btn-close" onClick={() => setSuccess(null)} aria-label="Close"></button>
                        </div>
                    )}

                    <div className="row">
                        <div className="col-lg-3 mb-4">
                            <div className="card">
                                <div className="card-body p-0">
                                    <div className="list-group list-group-flush">
                                        <div className="list-group-header sticky-top">Settings Categories</div>
                                        {tabs.map((tab) => (
                                            <button
                                                key={tab.id}
                                                className={`list-group-item list-group-item-action d-flex align-items-center ${activeTab === tab.id ? "active" : ""}`}
                                                onClick={() => setActiveTab(tab.id)}
                                                style={{
                                                    border: "none",
                                                    transition: "all 0.3s ease",
                                                }}
                                            >
                                                <span className="me-2" style={{ fontSize: "1.2rem" }}>{tab.icon}</span>
                                                <div className="flex-fill d-flex align-items-center gap-2">
                                                    <div className="font-weight-medium">{tab.label}</div>
                                                    {tab.isNew && <span className="badge sb-new-flag settings-new-flag">New</span>}
                                                </div>
                                                {unsavedChanges[tab.category] && (
                                                    <span className="badge bg-warning-lt">Unsaved</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-9">
                            {activeTab === "general" && (
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">General Settings</h3>
                                        <div className="card-actions">
                                            <button className="btn btn-primary btn-sm" onClick={() => handleSubmit("General")} disabled={!unsavedChanges["General"]}>
                                                Save Changes
                                            </button>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Club Logo</label>
                                                    <div className="input-group">
                                                        <input type="file" className="form-control" accept="image/*" onChange={handleLogoChange} />
                                                    </div>
                                                    <small className="form-hint">
                                                        Logo must be nearly square (aspect ratio 0.9–1.1) and at least 100x100 pixels, otherwise it will be displayed in a squeezed way
                                                    </small>
                                                    {logoPreview && (
                                                        <div className="mt-2">
                                                            <img src={logoPreview} alt="Logo Preview" style={{ maxWidth: "100px", maxHeight: "100px", borderRadius: "4px" }} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label required">Default Currency</label>
                                                    <select className="form-select" value={formData.default_currency || ""} onChange={(e) => handleInputChange("default_currency", e.target.value)}>
                                                        {CURRENCIES.map((currency) => (
                                                            <option key={currency.code} value={currency.code}>{getCurrencyLabel(currency.code)}</option>
                                                        ))}
                                                    </select>
                                                    <small className="form-hint">Used for every amount in the panel — pricing, the dashboard overview, stats and exports. Changing it relabels amounts, it does not convert them.</small>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label required">Default Language</label>
                                                    <select className="form-select" value={formData.default_language || ""} onChange={(e) => handleInputChange("default_language", e.target.value)}>
                                                        <option value="en">🇬🇧 English</option>
                                                        <option value="fr">🇫🇷 French</option>
                                                    </select>
                                                    <small className="form-hint">Primary language for the system interface</small>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label required">Time Zone</label>
                                                    <select className="form-select" value={formData.time_zone || ""} onChange={(e) => handleInputChange("time_zone", e.target.value)}>
                                                        <option value="UTC">UTC (±00:00)</option>
                                                        <option value="America/New_York">New York (EST/EDT)</option>
                                                        <option value="Europe/London">London (GMT/BST)</option>
                                                        <option value="Asia/Tokyo">Tokyo (JST)</option>
                                                        <option value="Africa/Casablanca">Casablanca (WET/WEST)</option>
                                                    </select>
                                                    <small className="form-hint">Time zone for scheduling and timestamps</small>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label required">Club Operating Hours</label>
                                                    <div className="input-group">
                                                        <input type="time" className="form-control" value={formData.club_operating_hours?.open || ""} onChange={(e) => handleInputChange("club_operating_hours", e.target.value, "open")} />
                                                        <span className="input-group-text">to</span>
                                                        <input type="time" className="form-control" value={formData.club_operating_hours?.close || ""} onChange={(e) => handleInputChange("club_operating_hours", e.target.value, "close")} />
                                                    </div>
                                                    <small className="form-hint">Daily operating hours for the club</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === "financial" && (
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Financial Settings</h3>
                                        <div className="card-actions">
                                            <button className="btn btn-primary btn-sm" onClick={() => handleSubmit("Financial")} disabled={!unsavedChanges["Financial"]}>
                                                Save Changes
                                            </button>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <h4 className="mb-3">Game Pricing Configuration</h4>
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label d-flex align-items-center">
                                                        <span className="avatar avatar-xs me-2" style={{ backgroundColor: "#dc3545" }}>S</span>
                                                        Snooker Pricing (Per Game)
                                                    </label>
                                                    <div className="input-group">
                                                        <span className="input-group-text">{getCurrencySymbol(formData.default_currency)}</span>
                                                        <input type="text" className="form-control" value={formData.snooker_pricing_per_game || ""} onChange={(e) => handleInputChange("snooker_pricing_per_game", e.target.value)} placeholder={getCurrencyPlaceholder(formData.default_currency)} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label d-flex align-items-center">
                                                        <span className="avatar avatar-xs me-2" style={{ backgroundColor: "#6f42c1" }}>M</span>
                                                        Mini Snooker Pricing (Per Game)
                                                    </label>
                                                    <div className="input-group">
                                                        <span className="input-group-text">{getCurrencySymbol(formData.default_currency)}</span>
                                                        <input type="text" className="form-control" value={formData.mini_snooker_pricing_per_game || ""} onChange={(e) => handleInputChange("mini_snooker_pricing_per_game", e.target.value)} placeholder={getCurrencyPlaceholder(formData.default_currency)} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label d-flex align-items-center">
                                                        <span className="avatar avatar-xs me-2" style={{ backgroundColor: "#198754" }}>P</span>
                                                        Pool Pricing (Per Game)
                                                    </label>
                                                    <div className="input-group">
                                                        <span className="input-group-text">{getCurrencySymbol(formData.default_currency)}</span>
                                                        <input type="text" className="form-control" value={formData.pool_pricing_per_game || ""} onChange={(e) => handleInputChange("pool_pricing_per_game", e.target.value)} placeholder={getCurrencyPlaceholder(formData.default_currency)} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label d-flex align-items-center">
                                                        <span className="avatar avatar-xs me-2" style={{ backgroundColor: "#0dcaf0" }}>U</span>
                                                        US Pool Pricing (Per Game)
                                                    </label>
                                                    <div className="input-group">
                                                        <span className="input-group-text">{getCurrencySymbol(formData.default_currency)}</span>
                                                        <input type="text" className="form-control" value={formData.us_pool_pricing_per_game || ""} onChange={(e) => handleInputChange("us_pool_pricing_per_game", e.target.value)} placeholder={getCurrencyPlaceholder(formData.default_currency)} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label d-flex align-items-center">
                                                        <span className="avatar avatar-xs me-2" style={{ backgroundColor: "#ffc107" }}>H</span>
                                                        Heyball Pricing (Per Game)
                                                    </label>
                                                    <div className="input-group">
                                                        <span className="input-group-text">{getCurrencySymbol(formData.default_currency)}</span>
                                                        <input type="text" className="form-control" value={formData.chinese_pool_pricing_per_game || ""} onChange={(e) => handleInputChange("chinese_pool_pricing_per_game", e.target.value)} placeholder={getCurrencyPlaceholder(formData.default_currency)} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === "game" && (
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Game Management Settings</h3>
                                        <div className="card-actions">
                                            <button className="btn btn-primary btn-sm" onClick={() => handleSubmit("Game Management")} disabled={!unsavedChanges["Game Management"]}>
                                                Save Changes
                                            </button>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <h4 className="mb-3">Queue Display Configuration</h4>
                                        <div className="row g-3 mb-4">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Tables to Display in Queue</label>
                                                    <input type="number" className="form-control" value={formData.queue_tables_to_display || ""} onChange={(e) => handleInputChange("queue_tables_to_display", e.target.value)} placeholder="8" min="1" max="20" />
                                                    <small className="form-hint">Maximum number of tables shown in queue view</small>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Table Animation Duration</label>
                                                    <div className="input-group">
                                                        <input type="number" className="form-control" value={formData.queue_table_animation_duration || ""} onChange={(e) => handleInputChange("queue_table_animation_duration", e.target.value)} placeholder="3" min="1" max="10" />
                                                        <span className="input-group-text">seconds</span>
                                                    </div>
                                                    <small className="form-hint">Transition time between table updates</small>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Players per Table Display</label>
                                                    <input type="number" className="form-control" value={formData.queue_players_per_table || ""} onChange={(e) => handleInputChange("queue_players_per_table", e.target.value)} placeholder="10" min="2" max="20" />
                                                    <small className="form-hint">Maximum players shown per table</small>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Player Animation Duration</label>
                                                    <div className="input-group">
                                                        <input type="number" className="form-control" value={formData.queue_player_animation_duration || ""} onChange={(e) => handleInputChange("queue_player_animation_duration", e.target.value)} placeholder="2" min="1" max="10" />
                                                        <span className="input-group-text">seconds</span>
                                                    </div>
                                                    <small className="form-hint">Transition time for player list updates</small>
                                                </div>
                                            </div>
                                        </div>

                                        <hr className="my-4" />

                                        <h4 className="mb-3">Pass Codes</h4>
                                        <div className="row g-3 mb-4">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Code Activation</label>
                                                    <label className="form-check form-switch">
                                                        <input className="form-check-input" type="checkbox" checked={formData.code_activation === "true"} onChange={(e) => handleInputChange("code_activation", e.target.checked ? "true" : "false")} />
                                                        <span className="form-check-label">{formData.code_activation === "true" ? "Enabled" : "Disabled"}</span>
                                                    </label>
                                                    <small className="form-hint">Require pass codes when starting games.</small>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Paid Code</label>
                                                    <input type="text" className="form-control" inputMode="numeric" pattern="\d*" value={formData.paid_code || ""} onChange={(e) => handleInputChange("paid_code", e.target.value.replace(/\D/g, ""))} placeholder="Paid Code…" disabled={formData.code_activation !== "true"} />
                                                    <small className="form-hint">Digits only (max 8). Preserved even if deactivated.</small>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Gifted Code</label>
                                                    <input type="text" className="form-control" inputMode="numeric" pattern="\d*" value={formData.gifted_code || ""} onChange={(e) => handleInputChange("gifted_code", e.target.value.replace(/\D/g, ""))} placeholder="Gifted Code…" disabled={formData.code_activation !== "true"} />
                                                    <small className="form-hint">Digits only (max 8). Preserved even if deactivated.</small>
                                                </div>
                                            </div>
                                        </div>

                                        <hr className="my-4" />

                                        <h4 className="mb-3">Shot Timer Settings</h4>
                                        <GameTimersTable
                                            games={GAME_TYPES}
                                            formData={formData}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                            )}

                            {activeTab === "scoreboard" && <ScoreboardAppearanceEditor clubId={club_id} />}
                            {activeTab === "streaming" && (
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Streaming Settings</h3>
                                        <div className="card-actions">
                                            <button className="btn btn-primary btn-sm" onClick={() => handleSubmit("Streaming")} disabled={!unsavedChanges["Streaming"]}>
                                                Save Changes
                                            </button>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <StreamingThemePicker
                                            value={formData.streaming_theme || "welshopen"}
                                            onChange={(theme) => handleInputChange("streaming_theme", theme)}
                                            tournamentTitle={formData.tournament_title || ""}
                                            onTournamentTitleChange={(title) => handleInputChange("tournament_title", title)}
                                            displayBreaks={formData.display_breaks || "false"}
                                            onDisplayBreaksChange={(breaks) => handleInputChange("display_breaks", breaks)}
                                            playerNameFormat={formData.player_name_format || "full"}
                                            onPlayerNameFormatChange={(format) => handleInputChange("player_name_format", format)}
                                            playerNameSize={formData.player_name_size}
                                            onPlayerNameSizeChange={(size) => handleInputChange("player_name_size", size)}
                                            statsMode={formData.streaming_stats_mode || "auto"}
                                            onStatsModeChange={(mode) => handleInputChange("streaming_stats_mode", mode)}
                                        />
                                        <div className="alert alert-info mt-1 mb-0">
                                            <div className="d-flex">
                                                <div>
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon alert-icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" aria-hidden="true">
                                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                        <circle cx="12" cy="12" r="9"></circle>
                                                        <line x1="12" y1="8" x2="12" y2="12"></line>
                                                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                                    </svg>
                                                </div>
                                                <div className="ms-2">
                                                    <h4 className="alert-title">Streaming Integration</h4>
                                                    <div className="text-muted">These settings will be applied to all active streaming sessions and OBS overlays.</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === "statistics" && (
                                <div className="card statistics-settings-card">
                                    <div className="card-header">
                                        <div>
                                            <span className="statistics-settings-kicker">Reporting rules</span>
                                            <h3 className="card-title">Statistics Settings</h3>
                                        </div>
                                        <div className="card-actions">
                                            <button className="btn btn-primary btn-sm" onClick={() => handleSubmit("Statistics")} disabled={!unsavedChanges["Statistics"]}>
                                                Save Changes
                                            </button>
                                        </div>
                                    </div>
                                    <div className="card-body statistics-settings-body">
                                        <section className="qualification-setting is-enabled">
                                            <div className="qualification-setting-copy">
                                                <span className="qualification-setting-icon" aria-hidden="true">
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                        <circle cx="12" cy="12" r="8.5" />
                                                        <circle cx="12" cy="12" r="3" />
                                                        <path d="M12 3.5v2M20.5 12h-2M12 20.5v-2M3.5 12h2" />
                                                    </svg>
                                                </span>
                                                <div>
                                                    <span className="qualification-setting-eyebrow">Frame collection</span>
                                                    <h4>Count every saved frame</h4>
                                                    <p>Overview and Performance always include every saved frame. Each snooker frame is separately marked Qualified or Not qualified.</p>
                                                </div>
                                            </div>
                                            <span className="badge bg-green-lt">Always on</span>
                                        </section>

                                        <div className="qualification-rules" aria-label="Frame reporting rules">
                                            <div><strong>Every frame</strong><span>Collected and included in totals</span></div>
                                            <div><strong>Clear status</strong><span>Qualified or Not qualified</span></div>
                                            <div><strong>One shared total</strong><span>Overview and Performance agree</span></div>
                                        </div>

                                        <div className="qualification-setting-note">
                                            <strong>No hidden frames</strong>
                                            <span>Qualification is informational only. A Not qualified frame remains visible and continues to affect frame counts and payment totals.</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .form-label.required:after { content: " *"; color: #d63939; }
                .list-group-item { 
                    background-color: transparent; 
                    color: var(--tblr-body-color); 
                    border: none; 
                    padding: .75rem 1rem; 
                    opacity: 0.8;
                }
                .list-group-item:hover:not(.active) { 
                    background-color: var(--tblr-bg-surface-secondary) !important; 
                    color: var(--tblr-body-color) !important; 
                    opacity: 1;
                }
                .list-group-item.active { 
                    background-color: var(--panel-mint, var(--tblr-primary)) !important;
                    color: var(--panel-green-deep, white) !important;
                    opacity: 1;
                }
                .card { 
                    background-color: var(--panel-surface, var(--tblr-card-bg));
                    border: 1px solid var(--panel-border, var(--tblr-border-color));
                    border-radius: 16px;
                    box-shadow: 0 10px 28px rgba(23, 43, 36, 0.045);
                    transition: box-shadow .2s; 
                }
                .card:hover { 
                    box-shadow: var(--tblr-box-shadow); 
                }
                .card-header { 
                    background-color: var(--panel-surface-soft, var(--tblr-bg-surface-secondary));
                    border-bottom: 1px solid var(--panel-border, var(--tblr-border-color));
                }
                .form-control, .form-select { 
                    background-color: var(--tblr-bg-forms); 
                    border-color: var(--tblr-border-color); 
                    color: var(--tblr-body-color); 
                }
                .form-control:focus, .form-select:focus { 
                    background-color: var(--tblr-bg-forms); 
                    border-color: var(--panel-green, var(--tblr-primary));
                    box-shadow: 0 0 0 .2rem rgba(23, 108, 82, .1);
                    color: var(--tblr-body-color); 
                }
                .input-group-text { 
                    background-color: var(--tblr-bg-surface-secondary); 
                    border-color: var(--tblr-border-color); 
                    color: var(--tblr-body-color); 
                    opacity: 0.8;
                }
                .badge { font-weight: 500; padding: .35em .65em; }
                .alert { border: none; border-radius: 8px; }
                .btn { border-radius: 6px; font-weight: 500; transition: all .2s; }
                .btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: var(--tblr-box-shadow); }
                .btn:disabled { opacity: .5; cursor: not-allowed; transform: none; box-shadow: none; }
                .avatar-xs { 
                    width: 1.5rem; 
                    height: 1.5rem; 
                    font-size: .75rem; 
                    display: flex; 
                    align-items: center; 
                    justify-content: center; 
                    border-radius: 50%; 
                    color: white; 
                    font-weight: bold; 
                }
                .form-hint { 
                    display: block; 
                    margin-top: .25rem; 
                    font-size: 85%; 
                    color: var(--tblr-body-color); 
                    opacity: 0.6;
                }
                .form-check-input { 
                    background-color: var(--tblr-bg-forms); 
                    border-color: var(--tblr-border-color); 
                }
                .form-check-input:checked { 
                    background-color: var(--panel-green, var(--tblr-primary));
                    border-color: var(--panel-green, var(--tblr-primary));
                }
                .list-group-header { 
                    background-color: var(--tblr-bg-surface-secondary); 
                    color: var(--tblr-body-color); 
                    opacity: 0.7;
                    text-transform: uppercase; 
                    font-size: .75rem; 
                    font-weight: 600; 
                    letter-spacing: .5px; 
                    padding: .75rem 1rem; 
                    border-bottom: 1px solid var(--tblr-border-color); 
                }
                option { 
                    background-color: var(--tblr-bg-forms); 
                    color: var(--tblr-body-color); 
                }
                @media (max-width: 991px) { .card-actions { margin-top: 1rem; } }
                .game-timers-table {
                    overflow: hidden;
                    border-radius: 10px;
                    border: 1px solid var(--tblr-border-color);
                }
                .game-timers-table .table {
                    border-collapse: separate;
                    border-spacing: 0;
                }
                .game-timers-table thead th {
                    background: var(--tblr-bg-surface-secondary);
                    border-bottom: 1px solid var(--tblr-border-color);
                    letter-spacing: .04em;
                    text-transform: uppercase;
                    font-weight: 700;
                    color: var(--tblr-body-color);
                    opacity: 0.8;
                }
                .game-timers-table tbody tr:hover {
                    background-color: var(--tblr-bg-surface-secondary);
                }
                .game-timers-table .input-group-text {
                    min-width: 82px;
                    justify-content: center;
                }
                .form-hint {
                    display: block;
                    margin-top: .35rem;
                    line-height: 1.25;
                    color: var(--tblr-body-color);
                    opacity: 0.6;
                }
                .statistics-settings-card .card-header {
                    align-items: center;
                    padding: 1.35rem 1.5rem;
                }
                .statistics-settings-kicker,
                .qualification-setting-eyebrow {
                    display: block;
                    color: var(--panel-green, #176c52);
                    font-size: .72rem;
                    font-weight: 800;
                    letter-spacing: .13em;
                    text-transform: uppercase;
                }
                .statistics-settings-kicker { margin-bottom: .3rem; }
                .statistics-settings-body { padding: 1.5rem; }
                .qualification-setting {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 2rem;
                    padding: 1.5rem;
                    border: 1px solid var(--panel-border, var(--tblr-border-color));
                    border-radius: 18px;
                    background: linear-gradient(135deg, rgba(235, 250, 242, .95), rgba(255, 255, 255, .98));
                }
                .qualification-setting.is-disabled {
                    background: linear-gradient(135deg, rgba(247, 248, 245, .98), rgba(255, 255, 255, .98));
                }
                .qualification-setting-copy {
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;
                    max-width: 650px;
                }
                .qualification-setting-icon {
                    display: grid;
                    place-items: center;
                    width: 50px;
                    height: 50px;
                    flex: 0 0 50px;
                    border-radius: 14px;
                    color: #173a30;
                    background: #c4f45b;
                    box-shadow: 0 10px 22px rgba(101, 155, 52, .16);
                }
                .qualification-setting-icon svg { width: 25px; height: 25px; }
                .qualification-setting h4 {
                    margin: .3rem 0 .35rem;
                    color: var(--panel-green-deep, #172b24);
                    font-size: 1.12rem;
                    line-height: 1.3;
                }
                .qualification-setting p {
                    margin: 0;
                    color: #66766f;
                    font-size: .92rem;
                    line-height: 1.6;
                }
                .qualification-toggle {
                    display: flex;
                    align-items: center;
                    gap: .75rem;
                    margin: 0;
                    color: var(--panel-green-deep, #172b24);
                    font-size: .84rem;
                    font-weight: 800;
                    cursor: pointer;
                }
                .qualification-toggle input {
                    position: absolute;
                    inline-size: 1px;
                    block-size: 1px;
                    opacity: 0;
                }
                .qualification-toggle i {
                    position: relative;
                    width: 52px;
                    height: 30px;
                    border-radius: 999px;
                    background: #cbd4cf;
                    transition: background .2s ease;
                }
                .qualification-toggle i::after {
                    content: "";
                    position: absolute;
                    top: 4px;
                    left: 4px;
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    background: white;
                    box-shadow: 0 3px 8px rgba(20, 45, 36, .2);
                    transition: transform .2s ease;
                }
                .qualification-toggle input:checked + i { background: var(--panel-green, #176c52); }
                .qualification-toggle input:checked + i::after { transform: translateX(22px); }
                .qualification-toggle input:focus-visible + i { outline: 3px solid rgba(196, 244, 91, .65); outline-offset: 3px; }
                .qualification-rules {
                    display: grid;
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                    gap: .8rem;
                    margin-top: 1rem;
                }
                .qualification-rules div {
                    padding: 1rem 1.1rem;
                    border: 1px solid var(--panel-border, var(--tblr-border-color));
                    border-radius: 14px;
                    background: var(--panel-surface-soft, #f7f9f5);
                }
                .qualification-rules strong,
                .qualification-rules span { display: block; }
                .qualification-rules strong { color: var(--panel-green-deep, #172b24); font-size: .94rem; }
                .qualification-rules span { margin-top: .2rem; color: #728078; font-size: .78rem; line-height: 1.45; }
                .qualification-setting-note {
                    display: flex;
                    gap: .75rem;
                    margin-top: 1rem;
                    padding: .9rem 1rem;
                    border-left: 3px solid #c4f45b;
                    color: #64736c;
                    background: rgba(196, 244, 91, .08);
                    font-size: .82rem;
                    line-height: 1.55;
                }
                .qualification-setting-note strong { flex: 0 0 auto; color: var(--panel-green, #176c52); }
                @media (max-width: 768px) {
                    .qualification-setting { align-items: flex-start; flex-direction: column; gap: 1.25rem; padding: 1.15rem; }
                    .qualification-setting-copy { gap: .8rem; }
                    .qualification-setting-icon { width: 44px; height: 44px; flex-basis: 44px; }
                    .qualification-rules { grid-template-columns: 1fr; }
                    .qualification-setting-note { flex-direction: column; gap: .25rem; }
                    .game-timers-table .table thead { display: none; }
                    .game-timers-table .table tbody tr {
                        display: block;
                        margin: .75rem;
                        margin-bottom: 1rem;
                        border: 1px solid var(--tblr-border-color);
                        border-radius: 10px;
                        padding: .75rem .75rem .5rem;
                        background: var(--tblr-bg-surface-secondary);
                    }
                    .game-timers-table .table tbody td {
                        display: block;
                        padding: .4rem 0 !important;
                        border: 0 !important;
                        align-items: flex-start;
                    }
                    .game-timers-table .table tbody td::before {
                        content: attr(data-label);
                        display: block;
                        font-weight: 600;
                        margin-bottom: .25rem;
                        color: var(--tblr-body-color);
                        opacity: 0.7;
                        letter-spacing: .02em;
                    }
                    .game-timers-table .input-group {
                        width: 100%;
                    }
                    .game-timers-table .form-hint {
                        font-size: .8rem;
                        margin-top: .3rem;
                        color: var(--tblr-body-color);
                        opacity: 0.6;
                    }
                    .game-timers-table .input-group-text {
                        min-width: 70px;
                        padding: .25rem .5rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default SettingsPage;
